import { LoaderCircle, type LucideIcon } from 'lucide-react';
import type { ComponentPropsWithRef } from 'react';
import { cx } from '../../lib/cx.ts';
import type { Side } from './position.ts';
import { Tooltip } from './Tooltip.tsx';
import './Button.css';

export type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger' | 'inverse' | 'inverse-ghost';
export type ButtonSize = 'sm' | 'md' | 'lg';

export interface ButtonProps extends ComponentPropsWithRef<'button'> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  /** Shows a spinner, disables the button and sets aria-busy. */
  loading?: boolean;
  icon?: LucideIcon;
  iconEnd?: LucideIcon;
  block?: boolean;
}

export function Button({
  variant = 'secondary',
  size = 'md',
  loading = false,
  icon: Icon,
  iconEnd: IconEnd,
  block = false,
  className,
  children,
  disabled,
  type = 'button',
  ...rest
}: ButtonProps) {
  const hasLabel = children !== undefined && children !== null && children !== false;
  return (
    <button
      {...rest}
      type={type}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      className={cx(
        'btn',
        `btn--${variant}`,
        size !== 'md' && `btn--${size}`,
        block && 'btn--block',
        !hasLabel && 'btn--icon-only',
        loading && 'is-loading',
        className,
      )}
    >
      {loading ? (
        <LoaderCircle className="spin" size={18} strokeWidth={1.75} aria-hidden="true" />
      ) : Icon ? (
        <Icon size={18} strokeWidth={1.75} aria-hidden="true" />
      ) : null}
      {hasLabel && <span className="btn__label">{children}</span>}
      {IconEnd && !loading ? <IconEnd size={18} strokeWidth={1.75} aria-hidden="true" /> : null}
    </button>
  );
}

export interface IconButtonProps extends Omit<ComponentPropsWithRef<'button'>, 'children' | 'aria-label'> {
  icon: LucideIcon;
  /** Accessible name (Thai). Also used as the tooltip text. */
  label: string;
  variant?: 'ghost' | 'secondary' | 'primary' | 'inverse-ghost';
  size?: 'sm' | 'md';
  /** Small red notification dot. */
  dot?: boolean;
  /** Numeric badge; include the count in `label` for screen readers. */
  count?: number;
  tooltip?: boolean;
  tooltipSide?: Side;
  loading?: boolean;
}

export function IconButton({
  icon: Icon,
  label,
  variant = 'ghost',
  size = 'md',
  dot = false,
  count,
  tooltip = true,
  tooltipSide = 'bottom',
  loading = false,
  className,
  disabled,
  type = 'button',
  ...rest
}: IconButtonProps) {
  const button = (
    <button
      {...rest}
      type={type}
      aria-label={label}
      aria-busy={loading || undefined}
      disabled={disabled || loading}
      className={cx('icon-btn', `icon-btn--${variant}`, size === 'sm' && 'icon-btn--sm', className)}
    >
      {loading ? (
        <LoaderCircle className="spin" size={size === 'sm' ? 16 : 18} strokeWidth={1.75} aria-hidden="true" />
      ) : (
        <Icon size={size === 'sm' ? 16 : 18} strokeWidth={1.75} aria-hidden="true" />
      )}
      {count !== undefined && count > 0 ? (
        <span className="icon-btn__count num" aria-hidden="true">
          {count > 99 ? '99+' : count}
        </span>
      ) : dot ? (
        <span className="icon-btn__dot" aria-hidden="true" />
      ) : null}
    </button>
  );
  return tooltip ? (
    <Tooltip content={label} side={tooltipSide} describeChild={false}>
      {button}
    </Tooltip>
  ) : (
    button
  );
}

export function Spinner({ size = 18, label = 'กำลังโหลด', className }: { size?: number; label?: string; className?: string }) {
  return (
    <span role="status" className={cx('spinner', className)}>
      <LoaderCircle className="spin" size={size} strokeWidth={1.75} aria-hidden="true" />
      <span className="visually-hidden">{label}</span>
    </span>
  );
}
